import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { HomeServiceCard, HomeServicesData } from "@/lib/homepage";

type ServicesProps = {
  data?: HomeServicesData;
};

const DEFAULT_SERVICES_DATA: HomeServicesData = {
  kicker: "Services",
  heading: "Your business finances on overdrive",
  subheading: "Proven expertise drives unparalleled results.",
  cards: [
    {
    title: "Currency Orchestration",
    description: "Currency Orchestration is a comprehensive framework of tools and services designed to optimize currency management, streamline global financial operations, and automate complex workflows.",
    icon: "O"
  },
  {
    title: "Currency Transformation", 
    description: "This service is centered around optimizing the process of converting and transferring currency. It provides businesses with tools to securely move money across borders to a wide range of countries and optimize for the best FX rates.",
    icon: "M"
  },
  {
    title: "Fraud Advisement",
    description: "Our Fraud Advisement team has decades of experience in managing Risk within refined processes and technologies to insure that your Currency flows steer clear of bad actors.", 
    icon: "V"
  },
  {
    title: "Training",
    description: "As part of the partner program, comprehensive training is provided to ensure a deep understanding of currency orchestration concepts and solutions. This is designed to help partners and their teams successfully sell and implement the platform.",
    icon: "D"
  },
  {
    title: "Integrations",
    description: "The platform is built to be configurable, allowing for seamless connections and integrations with existing systems and financial partners. It allows developers to extend the platform's functionality by building applications using available API's.",
    icon: ";"
  },
  {
    title: "Support", 
    description: "Expert Support is a key benefit of the partner program, providing partners with access to a team of currency experts. This support is designed to help partners successfully engage clients and address their most complex financial challenges.",
    icon: "A"
  },
  ],
};

function isExternalUrl(url: string) {
  return /^(https?:)?\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");
}

function ServiceCard({ card }: { card: HomeServiceCard }) {
  const { title, description, icon, learnMoreUrl } = card;
  const learnMoreLabel = title ? `Learn more about ${title}` : "Learn more about this service";

  const learnMore = learnMoreUrl
    ? isExternalUrl(learnMoreUrl)
      ? (
          <a
            href={learnMoreUrl}
            aria-label={learnMoreLabel}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 text-white text-sm group-hover:bg-white/25 transition-colors"
          >
            Learn More
            <ArrowRight className="h-4 w-4" />
          </a>
        )
      : (
          <Link
            to={learnMoreUrl}
            aria-label={learnMoreLabel}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 text-white text-sm group-hover:bg-white/25 transition-colors"
          >
            Learn More
            <ArrowRight className="h-4 w-4" />
          </Link>
        )
    : (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 text-white text-sm">
          Learn More
          <ArrowRight className="h-4 w-4" />
        </div>
      );

  return (
    <div className="group p-6 rounded-xl border border-white/8 bg-gradient-to-b from-white/3 to-transparent hover:border-white/15 transition-all duration-300 text-left serviceCard-Outter">
      <div className="serviceCard-Inner">
        <div className="space-y-4">
          {icon ? (
            <div className="text-7xl text-white/60 group-hover:text-white/100 transition-colors onplexus-icon">
              {icon}
            </div>
          ) : null}
          <h3 className="text-xl font-bold text-white">
            {title}
          </h3>
          {description ? (
            <p className="text-sm text-white/70 leading-relaxed">{description}</p>
          ) : null}
        </div>
        {learnMore}
      </div>
    </div>
  );
}

export default function Services({ data }: ServicesProps) {
  const resolved: HomeServicesData = {
    kicker: data?.kicker || DEFAULT_SERVICES_DATA.kicker,
    heading: data?.heading || DEFAULT_SERVICES_DATA.heading,
    subheading: data?.subheading || DEFAULT_SERVICES_DATA.subheading,
    cards:
      data?.cards && data.cards.length > 0
        ? data.cards
        : DEFAULT_SERVICES_DATA.cards,
  };
  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center space-y-12">
          {/* Section Header */}
          <div className="space-y-6">
            <div className="flex items-center justify-center gap-3">
              <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
              <span className="text-sm font-bold text-onplexus-gold-bright tracking-wider">
                {resolved.kicker}
              </span>
              <div className="h-0.5 w-24 bg-gradient-to-l from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">
                {resolved.heading}
              </h2>
              <p className="text-2xl text-white/70">
                {resolved.subheading}
              </p>
            </div>
          </div>
          
          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resolved.cards.map((card, index) => (
              <ServiceCard key={`${card.title ?? index}-${index}`} card={card} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
